import { useCallback, useMemo } from 'react';

import { useTranslation } from '@tecma/i18n';

import { useAppSelector } from 'redux/hooks';
import { sliceEnvironment } from 'redux/slice/slice.environment';

type UseSupportHeaderActionArgs = {
  isCustomerSupportEnabled?: boolean;
};

/**
 * Builds the `headerActions` payload for the Support button in the top header.
 *
 * Visibility rules:
 * - `isCustomerSupportEnabled` must be truthy.
 * - `REACT_APP_SUPPORT_BUTTON_TARGET_URL` must be a non-empty string.
 *
 * Behavior:
 * - Returns an empty array when the button must be hidden.
 * - Returns one header action (`headset` icon, `Support` label) when visible.
 * - Opens the target URL in a new tab with `noopener,noreferrer`.
 */
export const useSupportHeaderAction = ({ isCustomerSupportEnabled }: UseSupportHeaderActionArgs) => {
  const environment = useAppSelector(sliceEnvironment.selectors.environment);
  const { t } = useTranslation();
  const supportTargetUrl = environment.REACT_APP_SUPPORT_BUTTON_TARGET_URL?.trim();
  const isSupportButtonVisible = Boolean(isCustomerSupportEnabled) && Boolean(supportTargetUrl);

  const onSupportClick = useCallback(() => {
    if (!supportTargetUrl) {
      return;
    }
    window.open(supportTargetUrl, '_blank', 'noopener,noreferrer');
  }, [supportTargetUrl]);

  return useMemo(
    () =>
      isSupportButtonVisible
        ? [
            {
              isVisible: true,
              iconName: 'headset',
              label: t('menu.support', { defaultValue: 'Support' }),
              onClick: onSupportClick,
            },
          ]
        : [],
    [isSupportButtonVisible, onSupportClick, t],
  );
};
